<template>
  <v-page page="searchAccounts" title="Search Accounts">
    <v-container class="px-0 mx-0 pt-1 mt-3 pr-3 pl-3">
      <v-layout>
        <v-flex>
          <v-text-field v-model="query"
                        placeholder="Search"
                        clearable solo
                        @keydown.enter="search"
                        data-test="searchInput"></v-text-field>
        </v-flex>
        <v-flex xs2 class="ml-2">
          <v-select v-model="attribute"
                    :items="attributeChoices"
                    @change="search" solo></v-select>
        </v-flex>
        <v-flex class="searchbar-btn" xs2 lg1 pl-2>
          <v-btn @click="search" color="primary">Search</v-btn>
        </v-flex>
        <v-flex class="searchbar-btn" xs2 lg1>
          <paste-multiple @paste="paste" icon="" :parser="multipleParser"/>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex>
          <v-toolbar flat class="update-action">
            <v-spacer></v-spacer>
            <v-progress-linear class="mx-4" :indeterminate="true" v-if="loadingSelectAll"></v-progress-linear>
            <span v-else-if="showSelectAllToolbar">
              All <strong>{{ pagination.rowsPerPage }}</strong> accounts on this page selected.
              <a @click="selectAllAccounts">Select all {{ pagination.totalItems }} accounts corresponding to this search.</a>
            </span>
            <span v-else-if="selectedAllAccounts">
              All <strong>{{ pagination.totalItems }}</strong> accounts corresponding to this search selected.
              <a @click="clearSelection()">Clear selection</a>
            </span>
            <v-spacer></v-spacer>
            <v-csv-downloader :disabled="buttonsDisabled"
                              :fields="csvFields"
                              :datasource="csvData"
                              name="accounts"
                              :buttonText="`Download CSV (${updateData.length} selected)`"
                              customizable
                              class="d-flex"/>
            <AccountActions v-if="$can({ assessment: 'write' })" :accounts="updateData" :disabled="buttonsDisabled" class="d-flex ml-2"/>
            <EditAccounts :accounts="updateData" :onUpdate="searchAccounts" :disabled="buttonsDisabled" class="d-flex ml-2"/>
          </v-toolbar>
          <v-data-table v-model="selectedAccounts"
                        :headers="tableHeaders"
                        show-select
                        no-data-text="No accounts correspond to your search"
                        :loading="loading"
                        :items="accounts"
                        :options.sync="pagination"
                        :server-items-length="pagination.totalItems"
                        class="elevation-1 accounts-table"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc">
            <template slot="no-data">
              <div class="text-xs-center py-2">
                  No results
              </div>
            </template>
            <template v-slot:item="props">
              <tr :active="props.selected" @click="props.selected = !props.selected">
                <td v-if="$can({ assessment: 'read' })">
                  <v-checkbox :input-value="props.selected"
                              primary hide-details class="pb-3"></v-checkbox>
                </td>
                <td class="pr-0" style="border-left: 2px solid #eee">
                  <clipboard :value="props.item.username">{{ (props.item.username === props.item.token ? $options.filters.trimmer(props.item.username) : props.item.username) }}</clipboard>
                </td>
                <td>
                  {{ getUserAssessmentId(props.item) }}
                </td>
                <td>
                  {{ getAssessmentStatus(props.item) }}
                </td>
                <td class="pr-0">
                  <clipboard v-if="props.item.token" :value="props.item.token">{{ props.item.token | trimmer() }}</clipboard>
                  <span v-else>-</span>
                </td>
                <td>
                  {{ getLocation(props.item) }}
                </td>
                <td class="pr-0" v-if="$can({ task: 'read' })">
                  <template v-if="props.item.hasOwnProperty('assessments')">
                    <assessment-tasks :assessment="props.item.assessments[0]" />
                  </template>
                </td>
                <td>
                  <clipboard :value="props.item.created_by">{{ props.item.created_by }}</clipboard>
                </td>
                <td style="border-left: 3px solid #eee">
                  <v-btn @click="$goUserDetails(props.item.id)" icon text>
                    <v-icon aria-hidden="false" role="button">zoom_in</v-icon>
                  </v-btn>
                </td>
                <td class="pl-5" style="border-left: 3px solid #eee">
                  <template v-if="props.item.token">
                    <clipboard v-if="props.item.token" :value="linkedUrl(props.item, server)">
                      <external-link :href="linkedUrl(props.item, server)" hideText/>
                    </clipboard>
                  </template>
                  <template v-else>
                    <em>-</em>
                  </template>
                </td>
              </tr>
            </template>
            <template slot="headerCell" slot-scope="{ header }">
              {{ header.text }} &nbsp;
              <v-icon style="height: 16px"
                      v-if="header.value === 'password'"
                      @click="showAllPasswords = !showAllPasswords"
                      right>{{ showAllPasswords ? 'visibility_off' : 'visibility' }}
              </v-icon>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-container>
  </v-page>
</template>

<script>
import VPage from '../../layouts/Default'
import Clipboard from '@/components/Clipboard'
import { searchAccounts, getActiveAssessment, assessmentStatus } from '../../services/Accounts'
import AssessmentTasks from '@/components/Account/AssessmentTasks'
import EditAccounts from '@/components/Account/EditAccounts'
import AccountActions from '@/components/Account/AccountActions'
import PasteMultiple from '../../components/PasteMultiple'
import CurrentSession from '../../mixins/CurrentSession'
import { Bus } from '../../bus'
import { servers } from '@/helpers'
import ExternalLink from '@/components/ExternalLink'
import RequestCancelToken from '@/mixins/RequestCancelToken'
import LinkedUrl from '@/mixins/LinkedUrl'
import VCsvDownloader from '@/components/CsvDownloader/CsvDownloader.vue'
import UrlParser from '@/core/parsers/Url.parser'

export default {
  name: 'SearchAccounts',
  components: {
    VPage,
    Clipboard,
    AssessmentTasks,
    PasteMultiple,
    ExternalLink,
    VCsvDownloader,
    EditAccounts,
    AccountActions
  },
  mixins: [CurrentSession, RequestCancelToken, LinkedUrl],
  data () {
    return {
      details: 0,
      tooltip: false,
      query: '',
      sortBy: 'id',
      sortDesc: true,
      attribute: 'auto',
      attributeChoices: [
        { value: 'auto', text: 'Username/Token/URL' },
        { value: 'tokens', text: 'Token/URL' },
        { value: 'usernames', text: 'Username' },
        { value: 'locations', text: 'Location' },
        { value: 'assessment_ids', text: 'Assessment Id' },
        { value: 'assessment_groups', text: 'Assessment Group' },
        { value: 'created_by', text: 'Created By' }
      ],
      accounts: [],
      tableHeaders: [
        { text: 'Username', value: 'username' },
        { text: 'Active Assessment Id', value: 'user_assessment_id' },
        { text: 'Status', value: 'assessment_status' },
        { text: 'Token', value: 'token', sortable: false },
        { text: 'Location', value: 'location' },
        { text: 'Tasks', value: 'tasks', sortable: false, $can: { task: 'read' } },
        { text: 'Created By', value: 'created_by', sortable: false },
        { text: 'User Details', value: 'user_details', sortable: false },
        { text: 'Assessment Link', value: 'assessment_link', sortable: false }
      ],
      sessionItems: [
        { value: 'active', text: 'Reset incomplete' },
        { value: 'all', text: 'Reset all tasks' }
      ],
      sessionItem: null,
      closeOnClick: true,
      closeOnContentClick: true,
      showAllPasswords: false,
      selectedAccounts: [],
      loadingSelectAll: false,
      selectedAllAccounts: false,
      allAccountsData: {},
      loading: false,
      pagination: {
        descending: false,
        rowsPerPage: 10,
        page: 1,
        totalItems: 0
      },
      csvFields: {
        id: 'id',
        username: 'username',
        token: 'token',
        assessment_location: 'assessments.0.location',
        assessment_group: 'assessments.0.assessment_group',
        assessment_login_window: 'assessments.0.assessment_group',
        assessment_created_by: 'assessments.0.created_by',
        assessment_created_at: { 'attribute': 'assessments.0.created_at', formatter: 'date' },
        assessment_updated_by: 'assessments.0.updated_by',
        assessment_updated_at: { 'attribute': 'assessments.0.updated_at', formatter: 'date' },
        created_by: 'created_by',
        created_at: { 'attribute': 'created_by', formatter: 'date' },
        updated_by: 'updated_by',
        updated_at: { 'attribute': 'created_by', formatter: 'date' },
        game: this.csvLinksFormatter,
        tech_check: this.csvLinksFormatter
      },
      server: servers[this.$store.state.auth.server],
      userTypes: []
    }
  },
  created () {
    const types = this.$store.getters['users/userTypes']
    this.userTypes = Object.keys(types).map((key) => types[key])
  },
  computed: {
    /**
     * If true, the user will be given the option to select all accounts
     *
     * @returns {boolean}
     */
    showSelectAllToolbar () {
      return !this.selectedAllAccounts &&
        this.selectedAccounts.length === this.pagination.rowsPerPage &&
        this.selectedAccounts.length !== this.pagination.totalItems
    },
    /**
     * Returns the search query parameters to be sent to the backend
     */
    searchParameters () {
      const query = {
        limit: this.pagination.rowsPerPage,
        offset: (this.pagination.page - 1) * this.pagination.rowsPerPage,
        order: (this.pagination.descending ? '-' : '+') + this.pagination.sortBy
      }

      let attribute = this.attribute
      // Strip whitespace around commas
      let queryStrings = this.query.split(',')
      for (let i = 0; i < queryStrings.length; i++) {
        let string = queryStrings[i]
        if (string.substring(0, 4) === 'http') {
          string = UrlParser.tokens(string)
        }
        queryStrings[i] = string.trim()
      }

      if (attribute === 'auto') {
        if (queryStrings[0].length === 64) {
          attribute = 'tokens'
        } else {
          attribute = 'usernames'
        }
      }

      // Disable fuzzy search when searching
      // - for multiple
      // - or by username
      // - or by token
      if (queryStrings.length > 1 || attribute === 'usernames' || attribute === 'tokens') {
        query['exact'] = true
      }

      query[attribute] = queryStrings.join(',')

      return query
    },
    /**
     * Returns the initial data that will be passed to the update form
     */
    updateData () {
      return this.selectedAllAccounts ? this.allAccountsData : this.selectedAccounts
    },
    buttonsDisabled () {
      return this.selectedAccounts.length === 0
    }
  },
  watch: {
    /*
    * Turn off all accounts selection when individual accounts are selected
    */
    selectedAccounts () {
      this.selectedAllAccounts = false
    },
    pagination (newValue, oldValue) {
      this.searchAccounts()
    }
  },
  methods: {
    searchAccounts () {
      if (!this.query) {
        return
      }
      this.$set(this, 'loading', true)
      this.requestCancelTokenChecker()

      searchAccounts(this.searchParameters, this.requestToken.token)
        .then(({ users, total }) => {
          this.$set(this, 'loading', false)
          this.requestCancelTokenClearer()
          this.accounts = users
          this.pagination.totalItems = total
          this.selectedAllAccounts = false
        })
        .catch((error) => {
          if (error && error.message !== this.requestCancelMessage) {
            this.$set(this, 'loading', false)
            this.requestCancelTokenClearer()
            Bus.$emit('alert', ({ color: 'error', message: error }))
          }
        })
    },
    linkedUrl (item, server) {
      if (item.token) {
        const assessment = item.assessments[0]
        const assessmentConfigData = this.$store.getters['assessmentConfigs/assessmentConfigs'][assessment.assessment_config_id]

        if (assessmentConfigData && 'config' in assessmentConfigData && assessmentConfigData.config.is_mvp) {
          return `${server.a2e_mvp}/Admin?token=${item.token}`
        } else if (assessmentConfigData && 'is_a2e_assessment' in assessmentConfigData.config && 'is_mvp' in assessmentConfigData.config && !assessmentConfigData.config.is_mvp) {
          return `${server.a2e}/admin?token=${item.token}`
        } else {
          return `${server.tech_check}/?token=${item.token}`
        }
      }
    },
    multipleParser (pastedText) {
      return pastedText.split('\n').map(x => x.trim()).filter(x => !!x).join(', ')
    },

    clearSelection () {
      this.selectedAccounts = []
      this.selectedAllAccounts = false
    },

    /**
     * Search accounts (called when searchParameters are changed)
     */
    search () {
      this.selectedAccounts = []
      if (!this.query || this.query.length === 0) {
        this.accounts = []
      } else {
        this.searchAccounts()
      }
    },
    /**
     * Method called when pasting multiple accounts
     *
     * @param pasted
     */
    paste (pasted) {
      this.query = pasted
    },
    /**
     * Select all accounts corresponding to the current search (even those not appearing on the current page)
     */
    selectAllAccounts () {
      this.loadingSelectAll = true
      searchAccounts({ ...this.searchParameters, limit: -1, offset: 0 })
        .then(({ users }) => {
          this.allAccountsData = users
          this.selectedAllAccounts = true
          this.loadingSelectAll = false
        })
    },
    getLocation (account) {
      const activeAssessment = getActiveAssessment(account)
      if (activeAssessment) {
        return activeAssessment.location
      } else {
        return account.location
      }
    },
    getUserAssessmentId (account) {
      const activeAssessment = getActiveAssessment(account)
      if (activeAssessment) {
        return activeAssessment.id
      } else {
        return null
      }
    },
    getAssessmentStatus (account) {
      const activeAssessment = getActiveAssessment(account)
      if (activeAssessment) {
        const status = assessmentStatus(activeAssessment)
        if (status) {
          return status
        } else {
          return null
        }
      } else {
        return null
      }
    },

    /**
     * Returns the data of the CSV
     */
    csvData () {
      return this.updateData
    },
    csvLinksFormatter (key, row) {
      return this.linkedUrl(row, this.server)
    }
  }
}
</script>

<style lang="sass">
.update-action
  .v-toolbar__content
    padding: 0 !important

  .v-btn
    margin-right: 0 !important

.searchbar-btn button
  margin-top: 0 !important
  height: 55px
  min-height: 48px
  width: 94%
</style>
